import envConfig from "@/config/envConfig";
import { DEFAULT_LANGUAGE } from "@/data/constants";
import {
  QUERY_CATEGORIES_SLUG,
  QUERY_SUB_CATEGORIES_BY_CATEGORY,
  QUERY_SUB_CATEGORIES_SLUG,
} from "@/graphql/categories";
import { ArticleCategoryType, ArticleSubCategoryType } from "@/types/article";

export const fetchCategoriesSlug = async ({
  limit = 10,
}: {
  limit?: number;
  cache?: RequestCache;
}) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: QUERY_CATEGORIES_SLUG,
        variables: {
          limit,
        },
      }),
      next: { revalidate: 60 },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.Categories?.docs || []) as ArticleCategoryType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchSubCategoriesByCategoryId = async ({
  categoryId,
  limit = 10,
  locale,
}: {
  categoryId: string;
  limit?: number;
  locale?: string;
}) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: QUERY_SUB_CATEGORIES_BY_CATEGORY,
        variables: {
          categoryId,
          limit,
          locale,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.SubCategories?.docs || []) as ArticleSubCategoryType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchSubCategoriesSlug = async ({ limit = 10 }: { limit?: number }) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: QUERY_SUB_CATEGORIES_SLUG,
        variables: {
          limit,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.SubCategories?.docs || []) as ArticleSubCategoryType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchAllCategoriesWitSubCategories = async (lang?: string) => {
  try {
    const locale = lang && (lang === "te" || lang === "en") ? lang : "en";
    const url = `${envConfig.publicServerUrl}/api/v1/categories/list?lang=${locale}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error(`Expected JSON but received ${contentType || "unknown content type"}`);
    }

    const data = await response.json();

    return data;
  } catch (err) {
    console.error("Catch Error in fetchAllCategoriesWitSubCategories: ", err);
    return [];
  }
};

export const fetchSpecialCategories = async () => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/categories?where[isSpecialCategory][equals]=true`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const respData = await response.json();
    return (respData?.docs || []) as ArticleCategoryType[];
  } catch (err) {
    console.log("Catch Error in fetchSpecialCategories : ", err);
    throw err;
  }
};

// fetch sub category by ID
export const fetchSubCategorieByID = async ({ id }: { id: string }) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/categories/categoryid/${id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();

    return respData?.categories as Pick<ArticleSubCategoryType, "name">;
  } catch (err) {
    console.log(err);
  }
};

export const fetchSortedActiveCategories = async ({ limit }: { limit?: number }) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/categories?limit=${
        limit ?? 30
      }sort=sortOrder&where[status][equals]=active`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.docs || []) as ArticleCategoryType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchCategoryAndSubCategoryData = async ({
  id,
  lang = DEFAULT_LANGUAGE,
}: {
  id: string;
  lang?: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/blocks/categoryBreadcrumb/${id}?lang=${lang}`,
      {
        method: "GET",
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      } else {
        throw new Error(`Failed to fetch page data: ${response.statusText}`);
      }
    }
    const responseData = await response.json();

    return responseData;
  } catch (err) {
    console.log(err);
  }
};
